import React, { useState } from 'react';
import { V1SectionContainer } from './V1SectionContainer';
import { useSponsors } from '../../../hooks/useSponsors';
import { useEventContext } from '../../../contexts/EventContext';
import { Sponsor } from '../../../types/sponsor';
import { SponsorDetailModal } from '../../../components/sponsors/SponsorDetailModal';

interface Section {
  id: string;
  typeOf: string;
  customTitle?: string;
  sectionOption?: any;
  sectionData?: {
    sponsorsCount?: number;
    sponsorHeadings?: Record<string, number>;
  };
}

interface V1SponsorListSectionProps {
  section: Section;
}

interface SponsorGroup {
  heading: string;
  sponsors: Sponsor[];
  lowestPosition: number;
}

const SponsorLoadingSkeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
    {[...Array(8)].map((_, index) => (
      <div
        key={index}
        className="flex items-center justify-center p-4 border border-gray-100 rounded-lg"
      >
        <div className="w-32 h-16 bg-gray-200 animate-pulse rounded"></div>
      </div>
    ))}
  </div>
);

const SponsorGrid = ({ sponsors, displaySize, showHeading = true, isFirstInGroup = false, onSponsorClick }: {
  sponsors: Sponsor[],
  displaySize: 'small' | 'standard' | 'large',
  showHeading?: boolean,
  isFirstInGroup?: boolean,
  onSponsorClick: (sponsor: Sponsor) => void
}) => {
  // Use flex with justify-center for proper centering without empty grid cells
  // Add responsive padding on mobile to distinguish sponsor sizes
  const containerClass = displaySize === 'large'
    ? 'flex flex-wrap justify-center gap-8 px-4 sm:px-0' // Single sponsor per row for large with mobile padding
    : displaySize === 'standard'
    ? 'flex flex-wrap justify-center gap-6 px-6 sm:px-0' // 2 sponsors per row for standard with more mobile padding
    : 'flex flex-wrap justify-center gap-4 px-8 sm:px-0'; // 3 sponsors per row for small with most mobile padding

  // Define max widths for each size to control layout
  const cardClass = displaySize === 'large'
    ? 'w-full max-w-2xl' // Reduced from full width by ~40%
    : displaySize === 'standard'
    ? 'w-[calc(100%-3rem)] sm:w-[calc(50%-12px)] max-w-md' // Slightly smaller on mobile with side margins
    : 'w-[calc(50%-8px)] sm:w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] max-w-sm'; // Small: 2 per row on mobile, 3 on desktop

  const getLogoUrl = (sponsor: Sponsor) => {
    // Use different logo sizes based on display size
    if (displaySize === 'large') return sponsor.logoLargeUrl || sponsor.logoUrl;
    if (displaySize === 'small') return sponsor.logoMediumUrl || sponsor.logoUrl;
    return sponsor.logoMediumUrl || sponsor.logoUrl;
  };

  return (
    <div className={containerClass}>
      {sponsors.map((sponsor, index) => {
        const logoUrl = getLogoUrl(sponsor);

        if (!logoUrl) return null;

        // Only show heading for the first sponsor in the group
        const shouldShowHeading = isFirstInGroup && index === 0;
        const hasTextContent = sponsor.showName || sponsor.showDescription;

        const content = (
          <div className="relative w-full h-full overflow-hidden">
            {/* Blurred background image that fills the container */}
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={logoUrl}
                alt=""
                className="w-full h-full object-cover blur-xl scale-110 opacity-15"
                aria-hidden="true"
              />
              <div className="absolute inset-0 bg-white/30" />
            </div>

            {/* Main logo - edge to edge when no text content */}
            <div className={`relative h-full flex ${hasTextContent ? 'flex-col' : ''} items-center justify-center`}>
              <div className={`${hasTextContent ? 'flex-1' : 'w-full h-full'} flex items-center justify-center overflow-hidden`}>
                <img
                  src={logoUrl}
                  alt={sponsor.showName ? sponsor.name : 'Sponsor logo'}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Text content with blurred background */}
              {hasTextContent && (
                <div className="relative w-full">
                  {/* Blurred background for text */}
                  <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" />

                  <div className="relative px-4 py-3 text-center">
                    {sponsor.showName && (
                      <h3 className={`font-semibold text-gray-800 ${
                        displaySize === 'large' ? 'text-lg' :
                        displaySize === 'standard' ? 'text-base' :
                        'text-sm'
                      }`}>
                        {sponsor.name}
                      </h3>
                    )}
                    {sponsor.showDescription && sponsor.description && (
                      <div
                        className={`mt-1 text-gray-600 ${
                          displaySize === 'large' ? 'text-sm line-clamp-2' :
                          displaySize === 'standard' ? 'text-xs line-clamp-2' :
                          'text-xs line-clamp-1'
                        }`}
                        dangerouslySetInnerHTML={{ __html: sponsor.description }}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

        // Calculate aspect ratio class based on display size
        const aspectRatioClass = 'aspect-[2/1]'; // 2:1 aspect ratio for all sizes

        return (
          <div
            key={sponsor.id}
            onClick={() => onSponsorClick(sponsor)}
            className={`relative ${cardClass} ${aspectRatioClass} bg-white rounded-lg shadow-md hover:shadow-lg transition-all overflow-hidden cursor-pointer`}
          >
            {content}
          </div>
        );
      })}
    </div>
  );
};

/**
 * V1 Sponsor List Section
 *
 * Displays sponsor information with all V2 features including modals, click tracking,
 * and layout based on sponsor size.
 */
export const V1SponsorListSection: React.FC<V1SponsorListSectionProps> = ({ section }) => {
  const { eventSlug } = useEventContext();
  const { data: sponsorsResponse, isLoading, error } = useSponsors(eventSlug);
  const [selectedSponsor, setSelectedSponsor] = useState<Sponsor | null>(null);

  // The useSponsors hook returns the sponsors array directly
  const sponsors = sponsorsResponse || [];

  if (isLoading) {
    return (
      <V1SectionContainer section={section}>
        <h2 className="text-2xl font-bold mb-6">{section.customTitle || 'Sponsors'}</h2>
        <SponsorLoadingSkeleton />
      </V1SectionContainer>
    );
  }

  if (error) {
    return (
      <V1SectionContainer section={section}>
        <h2 className="text-2xl font-bold mb-6">{section.customTitle || 'Sponsors'}</h2>
        <div className="text-center py-8">
          <p className="text-gray-500">Unable to load sponsors at this time.</p>
        </div>
      </V1SectionContainer>
    );
  }

  if (sponsors.length === 0) {
    return (
      <V1SectionContainer section={section}>
        <h2 className="text-2xl font-bold mb-6">{section.customTitle || 'Sponsors'}</h2>
        <div className="text-center py-8">
          <p className="text-gray-500">No sponsors to display.</p>
        </div>
      </V1SectionContainer>
    );
  }

  // Group sponsors by heading
  const sponsorGroups: Record<string, SponsorGroup> = {};

  sponsors.forEach(sponsor => {
    const heading = sponsor.heading || sponsor.name;
    if (!sponsorGroups[heading]) {
      sponsorGroups[heading] = {
        heading,
        sponsors: [],
        lowestPosition: sponsor.position
      };
    }
    sponsorGroups[heading].sponsors.push(sponsor);
    // Track the lowest position value in this group
    if (sponsor.position < sponsorGroups[heading].lowestPosition) {
      sponsorGroups[heading].lowestPosition = sponsor.position;
    }
  });

  // Convert to array and sort by lowest position in each group
  const sortedGroups = Object.values(sponsorGroups).sort((a, b) => a.lowestPosition - b.lowestPosition);

  return (
    <V1SectionContainer section={section}>
      <h2 className="text-2xl font-bold mb-6">{section.customTitle || 'Sponsors'}</h2>

      {sortedGroups.map((group, groupIndex) => {
        // Within each group, sort sponsors by position
        const sortedSponsors = group.sponsors.sort((a, b) => a.position - b.position);

        // Group by display size within each heading group
        const largeSponsors = sortedSponsors.filter(s => s.displaySize === 'large');
        const standardSponsors = sortedSponsors.filter(s => s.displaySize === 'standard');
        const smallSponsors = sortedSponsors.filter(s => s.displaySize === 'small');

        return (
          <div key={group.heading} className={groupIndex > 0 ? 'mt-12' : ''}>
            {/* Only show group heading if there are multiple groups or if the heading differs from sponsor names */}
            {(sortedGroups.length > 1 || group.sponsors.some(s => s.heading && s.heading !== s.name)) && (
              <h3 className="text-xl font-semibold mb-6 text-center">{group.heading}</h3>
            )}

            {/* Display large sponsors first */}
            {largeSponsors.length > 0 && (
              <div className="mb-8">
                <SponsorGrid
                  sponsors={largeSponsors}
                  displaySize="large"
                  showHeading={true}
                  isFirstInGroup={true}
                  onSponsorClick={setSelectedSponsor}
                />
              </div>
            )}

            {/* Display standard sponsors */}
            {standardSponsors.length > 0 && (
              <div className={largeSponsors.length > 0 ? 'mb-8' : ''}>
                <SponsorGrid
                  sponsors={standardSponsors}
                  displaySize="standard"
                  showHeading={true}
                  isFirstInGroup={largeSponsors.length === 0}
                  onSponsorClick={setSelectedSponsor}
                />
              </div>
            )}

            {/* Display small sponsors */}
            {smallSponsors.length > 0 && (
              <div className={largeSponsors.length > 0 || standardSponsors.length > 0 ? 'mt-8' : ''}>
                <SponsorGrid
                  sponsors={smallSponsors}
                  displaySize="small"
                  showHeading={true}
                  isFirstInGroup={largeSponsors.length === 0 && standardSponsors.length === 0}
                  onSponsorClick={setSelectedSponsor}
                />
              </div>
            )}
          </div>
        );
      })}

      {/* Sponsor Detail Modal */}
      {selectedSponsor && (
        <SponsorDetailModal
          sponsor={selectedSponsor}
          isOpen={!!selectedSponsor}
          onClose={() => setSelectedSponsor(null)}
        />
      )}
    </V1SectionContainer>
  );
};